import React, { useState, useEffect, useCallback } from 'react';
import { useSetCurrentPage } from '../stores/useAppStore';
import GradientBackground from '../components/ui/GradientBackground';
import ParticleBackground from '../components/ui/ParticleBackground';
import PageHeader from '../components/PageHeader';
import { stockService, IntradaySignal } from '../services/stockService';
import { stockStrategyService, StockHistorySignal } from '../services/stockStrategyService';
import { useToastStore } from '../stores/useToastStore';

const StockSignals: React.FC = () => {
  const setCurrentPage = useSetCurrentPage();
  const showToast = useToastStore((state) => state.show);

  const [activeTab, setActiveTab] = useState<'live' | 'history'>('live');
  const [signals, setSignals] = useState<IntradaySignal[]>([]);
  const [isLoadingLive, setIsLoadingLive] = useState(false);

  // 历史信号相关状态
  const [historySignals, setHistorySignals] = useState<StockHistorySignal[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const historyLimit = 15;
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historySymbol, setHistorySymbol] = useState('');
  const [historyConfirmed, setHistoryConfirmed] = useState<boolean | undefined>(undefined);

  const [performanceStats, setPerformanceStats] = useState<any>(null);
  const [strategyAdvice, setStrategyAdvice] = useState<{
    status: 'normal' | 'warning' | 'bullish';
    message: string;
  } | null>(null);

  // 获取绩效统计并生成建议
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const stats = await stockStrategyService.getStockSignalPerformanceStats(30);
        setPerformanceStats(stats);

        if (stats.winRate < 45) {
          setStrategyAdvice({
            status: 'warning',
            message: '⚠️ 当前市场环境胜率较低，建议控制仓位，仅关注极强信号。',
          });
        } else if (stats.winRate > 65) {
          let msg = '🚀 策略近期胜率极高，市场进入主线爆发期，可积极关注共振标的。';
          if (stats.synergyStats && stats.synergyStats.winRate > 75) {
            msg = '🔥 板块共振胜率极高！优先关注具有行业背景的共振爆发信号。';
          }
          setStrategyAdvice({
            status: 'bullish',
            message: msg,
          });
        } else {
          setStrategyAdvice({
            status: 'normal',
            message: `✅ 策略表现稳定，近30日均益 +${(stats.avgPnl1d || 0).toFixed(2)}%，按原计划执行。`,
          });
        }
      } catch (err) {
        console.error('Failed to fetch performance stats:', err);
      }
    };
    fetchStats();
  }, []);

  const fetchHistorySignals = useCallback(
    async (page: number) => {
      try {
        setLoadingHistory(true);
        const offset = (page - 1) * historyLimit;
        const response = await stockStrategyService.getStockHistorySignals({
          limit: historyLimit,
          offset,
          symbol: historySymbol || undefined,
          isConfirmed: historyConfirmed,
        });
        if (response && response.signals) {
          setHistorySignals(response.signals);
          setHistoryTotal(response.total);
        }
      } catch (error) {
        console.error('Failed to fetch history signals:', error);
      } finally {
        setLoadingHistory(false);
      }
    },
    [historyLimit, historySymbol, historyConfirmed],
  );

  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistorySignals(historyPage);
    }
  }, [activeTab, historyPage, fetchHistorySignals]);

  // 获取实时信号的方法
  const fetchLiveSignals = useCallback(
    async (silent = false) => {
      try {
        if (!silent) setIsLoadingLive(true);
        const signalsData = await stockService.getIntradaySignals(undefined, undefined, 50);
        if (signalsData) {
          setSignals(signalsData);
        }
      } catch (error) {
        console.error('Failed to fetch intraday signals:', error);
        if (!silent) showToast('获取实时信号失败', 'error');
      } finally {
        if (!silent) setIsLoadingLive(false);
      }
    },
    [showToast],
  );

  // 定时获取实时信号
  useEffect(() => {
    fetchLiveSignals();
    const timer = setInterval(() => fetchLiveSignals(true), 30000); // 30秒更新一次
    return () => clearInterval(timer);
  }, [fetchLiveSignals]);

  const handlePageChange = (newPage: number) => {
    setHistoryPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <GradientBackground className="text-white">
      <ParticleBackground />

      <div className="fixed inset-0 flex flex-col overflow-hidden">
        {/* 1. 顶部标题栏 */}
        <PageHeader
          title="A股核心信号"
          onBack={() => setCurrentPage('stock-dashboard')}
          rightContent={
            <button
              onClick={() => {
                if (activeTab === 'live') fetchLiveSignals();
                else fetchHistorySignals(historyPage);
              }}
              className="flex items-center justify-center w-9 h-9 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white hover:bg-white/20 hover:border-white/30 transition-all duration-300 active:scale-95"
              title="刷新"
            >
              <svg
                className={`w-4 h-4 ${isLoadingLive || loadingHistory ? 'animate-spin' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          }
        />

        {/* 主体内容区域 */}
        <div className="flex-1 flex flex-col min-h-0 pt-[56px]">
          {/* 2. 状态/Tab 切换区 */}
          <div
            className="fixed left-0 right-0 z-40 bg-black/20 backdrop-blur-md border-b border-white/10 px-4 py-2 flex flex-wrap items-center justify-between gap-4 shadow-lg"
            style={{ top: '56px' }}
          >
            <div className="flex bg-white/5 p-1 rounded-xl border border-white/10 shadow-inner">
              <button
                onClick={() => setActiveTab('live')}
                className={`px-6 py-1.5 text-xs font-bold rounded-lg transition-all duration-300 ${
                  activeTab === 'live'
                    ? 'bg-white/20 text-white shadow-[0_0_15px_rgba(255,255,255,0.1)] ring-1 ring-white/20'
                    : 'text-white/40 hover:text-white/70 hover:bg-white/5'
                }`}
              >
                实时信号
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`px-6 py-1.5 text-xs font-bold rounded-lg transition-all duration-300 ${
                  activeTab === 'history'
                    ? 'bg-white/20 text-white shadow-[0_0_15px_rgba(255,255,255,0.1)] ring-1 ring-white/20'
                    : 'text-white/40 hover:text-white/70 hover:bg-white/5'
                }`}
              >
                历史信号
              </button>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-full border border-white/10 backdrop-blur-md">
                <div
                  className={`w-1.5 h-1.5 rounded-full animate-pulse ${
                    activeTab === 'live'
                      ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]'
                      : 'bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.6)]'
                  }`}
                />
                <span className="text-[10px] text-white/60 font-bold uppercase tracking-wider">
                  {activeTab === 'live' ? 'Live System' : 'Archive Mode'}
                </span>
              </div>
            </div>
          </div>

          {/* 3. 策略绩效与建议区/历史信号筛选区 */}
          <div className="flex-none pt-[52px]">
            {activeTab === 'live' && (performanceStats || strategyAdvice) && (
              <div className="px-4 py-3 bg-black/10 border-b border-white/5 z-20">
                {strategyAdvice && (
                  <div
                    className={`mb-3 p-3 rounded-xl border text-[11px] leading-relaxed font-medium transition-all ${
                      strategyAdvice.status === 'warning'
                        ? 'bg-orange-500/10 border-orange-500/20 text-orange-300'
                        : strategyAdvice.status === 'bullish'
                          ? 'bg-red-500/10 border-red-500/20 text-red-300'
                          : 'bg-blue-500/10 border-blue-500/20 text-blue-300'
                    }`}
                  >
                    {strategyAdvice.message}
                  </div>
                )}

                {performanceStats && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="flex items-center justify-between p-3 bg-white/5 border border-white/10 rounded-2xl">
                      <div>
                        <p className="text-white/40 text-[9px] uppercase tracking-widest mb-1">30日综合胜率</p>
                        <div className="flex items-baseline gap-2">
                          <span className="text-xl font-black text-red-400">
                            {Number(performanceStats.winRate || 0).toFixed(1)}%
                          </span>
                          <span className="text-[10px] text-white/20">样本: {performanceStats.total || 0}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-white/40 text-[9px] uppercase tracking-widest mb-1">平均最高涨幅</p>
                        <span className="text-xl font-black text-yellow-400">
                          +{Number(performanceStats.avgMaxPnl || 0).toFixed(1)}%
                        </span>
                      </div>
                    </div>

                    {performanceStats.byScore
                      ?.filter((s: any) => s.max === 100)
                      .map((highScore: any, idx: number) => (
                        <div
                          key={idx}
                          className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-2xl flex justify-between items-center"
                        >
                          <div>
                            <p className="text-purple-300/60 text-[9px] uppercase tracking-widest mb-1">
                              高分标的表现 (&gt;=85)
                            </p>
                            <div className="flex items-center gap-3">
                              <span className="text-base font-black text-purple-300">
                                {Number(highScore.winRate).toFixed(1)}% 胜率
                              </span>
                              <span className="text-xs font-bold text-purple-300/50">
                                均益 +{Number(highScore.avgPnl).toFixed(2)}%
                              </span>
                            </div>
                          </div>
                          <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center border border-purple-500/30">
                            <span className="text-xs font-black text-purple-300">{highScore.total}</span>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'history' && (
              <div className="px-4 py-2 bg-black/10 backdrop-blur-sm border-b border-white/10 flex items-center gap-2 z-20">
                <input
                  type="text"
                  placeholder="股票筛选"
                  value={historySymbol}
                  onChange={(e) => setHistorySymbol(e.target.value)}
                  className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white text-sm placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30"
                />

                <div className="flex bg-white/5 p-1 rounded-xl border border-white/10 inline-flex flex-shrink-0">
                  <button
                    onClick={() => setHistoryConfirmed(undefined)}
                    className={`px-3 py-1.5 rounded-lg text-[10px] font-bold transition-all ${
                      historyConfirmed === undefined
                        ? 'bg-white/20 text-white shadow-sm'
                        : 'text-white/40 hover:text-white/60'
                    }`}
                  >
                    全部
                  </button>
                  <button
                    onClick={() => setHistoryConfirmed(true)}
                    className={`px-3 py-1.5 rounded-lg text-[10px] font-bold transition-all ${
                      historyConfirmed === true
                        ? 'bg-red-500/20 text-red-400 shadow-sm'
                        : 'text-white/40 hover:text-white/60'
                    }`}
                  >
                    已确认
                  </button>
                  <button
                    onClick={() => setHistoryConfirmed(false)}
                    className={`px-3 py-1.5 rounded-lg text-[10px] font-bold transition-all ${
                      historyConfirmed === false
                        ? 'bg-white/20 text-white shadow-sm'
                        : 'text-white/40 hover:text-white/60'
                    }`}
                  >
                    未确认
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* 5. 主列表区域 */}
          <div className="flex-1 overflow-y-auto px-4 py-4 custom-scrollbar bg-black/5">
            {activeTab === 'live' ? (
              <div className="space-y-3">
                {isLoadingLive ? (
                  <div className="flex flex-col items-center justify-center py-20 text-white/20">
                    <div className="w-10 h-10 border-2 border-blue-500/20 border-t-blue-500 rounded-full animate-spin mb-4" />
                    <p className="text-xs font-bold uppercase tracking-widest">正在获取实时信号...</p>
                  </div>
                ) : signals.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-24 text-white/10">
                    <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mb-6 border border-white/5">
                      <span className="text-4xl">📡</span>
                    </div>
                    <p className="text-sm font-bold uppercase tracking-widest mb-2 text-white/30">市场扫描中</p>
                    <p className="text-xs text-white/20">目前暂无符合策略的实时信号</p>
                  </div>
                ) : (
                  signals.map((signal, i) => (
                    <div
                      key={i}
                      className="group relative overflow-hidden bg-white/5 border border-white/10 rounded-2xl p-4 hover:bg-white/10 hover:border-white/20 transition-all duration-300"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          {/* 动作标识 */}
                          <div
                            className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center shadow-2xl transition-transform group-hover:scale-105 flex-shrink-0 ${
                              signal.action === 'BUY'
                                ? 'bg-gradient-to-br from-red-500/30 to-red-600/10 text-red-100 border border-red-500/30'
                                : 'bg-gradient-to-br from-green-500/30 to-green-600/10 text-green-100 border border-green-500/30'
                            }`}
                          >
                            <span className="text-[8px] font-black uppercase tracking-tighter opacity-60 mb-0.5">
                              {signal.action === 'BUY' ? 'Buy' : 'Sell'}
                            </span>
                            <span className="text-sm font-black leading-none">
                              {signal.action === 'BUY' ? '买入' : '卖出'}
                            </span>
                          </div>

                          {/* 股票信息 */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h4 className="text-white text-base font-black tracking-tight truncate">
                                {signal.name || signal.symbol}
                              </h4>
                              <span className="px-1.5 py-0.5 rounded-md bg-white/10 text-white/50 text-[9px] font-mono border border-white/10">
                                {signal.symbol}
                              </span>
                              {signal.score && (
                                <div
                                  className={`px-1.5 py-0.5 rounded-md text-[9px] font-black shadow-sm ${
                                    signal.score >= 85
                                      ? 'bg-purple-500/30 text-purple-300 border border-purple-500/30'
                                      : signal.score >= 75
                                        ? 'bg-yellow-500/30 text-yellow-300 border border-yellow-500/30'
                                        : 'bg-white/10 text-white/60 border border-white/10'
                                  }`}
                                >
                                  评分 {signal.score}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-3 flex-wrap">
                              {signal.change !== undefined && signal.change !== null && (
                                <span
                                  className={`text-xs font-bold ${signal.change >= 0 ? 'text-red-400' : 'text-green-400'}`}
                                >
                                  {signal.change >= 0 ? '+' : ''}
                                  {Number(signal.change).toFixed(2)}%
                                </span>
                              )}
                              <p className="text-white/40 text-[11px] font-medium truncate max-w-[200px]">
                                {signal.reason || '核心策略触发'}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* 价格与时间 */}
                        <div className="text-right flex-shrink-0">
                          <div className="text-lg font-black text-white tracking-tighter mb-1 font-mono">
                            <span className="text-xs mr-0.5 text-white/30 font-normal">¥</span>
                            {Number(signal.price || 0).toFixed(2)}
                          </div>
                          <div className="flex items-center justify-end gap-1.5">
                            <div className="w-1 h-1 bg-blue-500 rounded-full animate-pulse" />
                            <span className="text-[10px] text-white/30 font-mono font-bold tracking-tight">
                              {new Date(signal.time).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {loadingHistory && historySignals.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 text-white/20">
                    <div className="w-10 h-10 border-2 border-blue-500/20 border-t-blue-500 rounded-full animate-spin mb-4" />
                    <p className="text-xs font-bold uppercase tracking-widest">加载历史记录...</p>
                  </div>
                ) : historySignals.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-24 text-white/10">
                    <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mb-6 border border-white/5">
                      <span className="text-4xl">📂</span>
                    </div>
                    <p className="text-sm font-bold uppercase tracking-widest mb-2 text-white/30">暂无历史信号</p>
                  </div>
                ) : (
                  historySignals.map((signal, i) => (
                    <div
                      key={i}
                      className="group relative overflow-hidden bg-white/5 border border-white/10 rounded-2xl p-4 hover:bg-white/10 transition-all duration-300"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          <div
                            className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center shadow-2xl transition-transform group-hover:scale-105 flex-shrink-0 ${
                              signal.isConfirmed
                                ? 'bg-gradient-to-br from-red-500/30 to-red-600/10 text-red-100 border border-red-500/30'
                                : 'bg-gradient-to-br from-gray-500/30 to-gray-600/10 text-gray-100 border border-gray-500/30'
                            }`}
                          >
                            <span className="text-[8px] font-black uppercase tracking-tighter opacity-60 mb-0.5">
                              {signal.strategy}
                            </span>
                            <span className="text-sm font-black leading-none">
                              {signal.isConfirmed ? '买入' : '扫描'}
                            </span>
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h4 className="text-white text-base font-black tracking-tight truncate">
                                {signal.name || signal.symbol}
                              </h4>
                              <span className="px-1.5 py-0.5 rounded-md bg-white/10 text-white/50 text-[9px] font-mono border border-white/10">
                                {signal.symbol}
                              </span>
                            </div>
                            <div className="flex items-center gap-3 flex-wrap">
                              <span className="text-[10px] text-white/40">
                                {new Date(signal.triggerTime).toLocaleString([], {
                                  month: '2-digit',
                                  day: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </span>
                              <div className="h-2 w-[1px] bg-white/10" />
                              <p className="text-white/40 text-[10px] font-medium truncate max-w-[200px]">
                                {signal.reason || '历史扫描信号'}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="text-right flex-shrink-0">
                          <div className="text-lg font-black text-white tracking-tighter mb-1 font-mono">
                            <span className="text-xs mr-0.5 text-white/30 font-normal">¥</span>
                            {Number(signal.price || 0).toFixed(2)}
                          </div>
                          {signal.pnl1d !== undefined && signal.pnl1d !== null && (
                            <span
                              className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${
                                signal.pnl1d >= 0 ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'
                              }`}
                            >
                              次日 {signal.pnl1d >= 0 ? '+' : ''}
                              {signal.pnl1d.toFixed(2)}%
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* 6. 分页控制 - 仅历史记录显示 */}
          {activeTab === 'history' && historyTotal > historyLimit && (
            <div className="flex-none px-4 py-2 bg-black/40 backdrop-blur-md border-t border-white/10 z-30">
              <div className="flex items-center justify-center space-x-2">
                <button
                  onClick={() => handlePageChange(Math.max(1, historyPage - 1))}
                  disabled={historyPage === 1 || loadingHistory}
                  className="px-4 py-1.5 rounded-lg bg-white/10 text-white/70 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-all active:scale-95 text-xs"
                >
                  上一页
                </button>
                <div className="flex items-center px-4 py-1.5 rounded-lg bg-white/5 text-white/90 border border-white/10">
                  <span className="text-[10px] font-medium">
                    第 {historyPage} 页 / 共 {Math.ceil(historyTotal / historyLimit)} 页
                  </span>
                </div>
                <button
                  onClick={() => handlePageChange(Math.min(Math.ceil(historyTotal / historyLimit), historyPage + 1))}
                  disabled={historyPage >= Math.ceil(historyTotal / historyLimit) || loadingHistory}
                  className="px-4 py-1.5 rounded-lg bg-white/10 text-white/70 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-all active:scale-95 text-xs"
                >
                  下一页
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </GradientBackground>
  );
};

export default StockSignals;
